import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, TrendingUp, TrendingDown, Wallet, Settings, Landmark, Scale } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const fmt = (n) => `Tsh ${Math.round(n || 0).toLocaleString()}`;

export default function Finance({ user }) {
  const isAdmin = user?.role?.toLowerCase() === 'admin';
  const [settings, setSettings] = useState({ fy_start_month: 1, expense_categories: [], cash_on_hand: 0,
    break_even: { price_per_unit: 0, variable_cost_per_unit: 0, monthly_fixed_costs: 0 } });

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/settings`, authHeaders());
      setSettings(res.data);
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  return (
    <div data-testid="finance-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Finance</h1>
        <p className="text-slate-600 mt-1">
          Expenses, purchases, assets, loans and profit &amp; loss — income from customer order payments is picked up automatically.
        </p>
      </div>
      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="pnl" data-testid="pnl-tab">Profit &amp; Loss</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="expenses-tab">Expenses</TabsTrigger>
          <TabsTrigger value="income" data-testid="income-tab">Other Income</TabsTrigger>
          <TabsTrigger value="purchases" data-testid="purchases-tab">Purchases</TabsTrigger>
          <TabsTrigger value="assets" data-testid="assets-tab">Assets</TabsTrigger>
          <TabsTrigger value="loans" data-testid="loans-tab">Loans</TabsTrigger>
          {isAdmin && <TabsTrigger value="settings" data-testid="fy-settings-tab">Settings</TabsTrigger>}
        </TabsList>
        <TabsContent value="overview"><Overview /></TabsContent>
        <TabsContent value="pnl"><ProfitLoss /></TabsContent>
        <TabsContent value="expenses"><Entries kind="expense" categories={settings.expense_categories} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="income"><Entries kind="income" categories={['sales', 'interest', 'grants', 'asset_disposal', 'other']} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="purchases"><SubModule cfg={SUBMODULES.purchases} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="assets"><SubModule cfg={SUBMODULES.assets} isAdmin={isAdmin} /></TabsContent>
        <TabsContent value="loans"><SubModule cfg={SUBMODULES.loans} isAdmin={isAdmin} /></TabsContent>
        {isAdmin && (
          <TabsContent value="settings">
            <FinanceSettings settings={settings} onSaved={fetchSettings} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Overview — dashboard, balance sheet and break-even                  */
/* ------------------------------------------------------------------ */

function Overview() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/finance/overview`, authHeaders())
      .then((res) => setData(res.data))
      .catch((error) => toast.error(error.response?.data?.detail || 'Failed to load overview'));
  }, []);

  if (!data) return <p className="text-gray-400 mt-4">Loading…</p>;

  const be = data.break_even;

  return (
    <div className="space-y-4 mt-2">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <TrendingUp className="text-green-600" />
          <div><p className="text-xs text-gray-500">Total Income</p>
            <p className="text-lg font-bold text-green-700" data-testid="ov-income">{fmt(data.income)}</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <TrendingDown className="text-red-500" />
          <div><p className="text-xs text-gray-500">Total Expenses</p>
            <p className="text-lg font-bold text-red-600" data-testid="ov-expenses">{fmt(data.expenses)}</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <Wallet className={data.net_profit >= 0 ? 'text-green-600' : 'text-red-500'} />
          <div><p className="text-xs text-gray-500">Net Profit ({Math.round(data.profit_margin)}%)</p>
            <p className={`text-lg font-bold ${data.net_profit >= 0 ? 'text-green-700' : 'text-red-600'}`}
              data-testid="ov-profit">{fmt(data.net_profit)}</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <Landmark className="text-blue-600" />
          <div><p className="text-xs text-gray-500">Cash Position</p>
            <p className="text-lg font-bold text-blue-700" data-testid="ov-cash">{fmt(data.cash)}</p></div>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card><CardContent className="pt-4">
          <h3 className="font-semibold mb-2 flex items-center gap-2"><Scale size={16} /> Balance Sheet</h3>
          <div className="text-sm space-y-1">
            <div className="flex justify-between py-1"><span>Cash (opening {fmt(data.opening_cash)} + profit)</span><span>{fmt(data.cash)}</span></div>
            <div className="flex justify-between py-1"><span>Fixed assets (gross)</span><span>{fmt(data.gross_assets)}</span></div>
            <div className="flex justify-between py-1 text-gray-500"><span>Less accumulated depreciation</span><span>−{fmt(data.accum_depreciation)}</span></div>
            <div className="flex justify-between py-1"><span>Fixed assets (net)</span><span>{fmt(data.net_fixed_assets)}</span></div>
            <div className="flex justify-between py-1 font-semibold border-t"><span>Total Assets</span><span>{fmt(data.total_assets)}</span></div>
            <div className="flex justify-between py-1 text-red-600"><span>Loans outstanding</span><span>{fmt(data.loan_balance)}</span></div>
            <div className="flex justify-between py-1 font-bold border-t"><span>Owner's Equity</span>
              <span className={data.equity >= 0 ? 'text-green-700' : 'text-red-600'}>{fmt(data.equity)}</span></div>
          </div>
        </CardContent></Card>

        <Card><CardContent className="pt-4">
          <h3 className="font-semibold mb-2">Break-even Analysis</h3>
          {be ? (
            <div className="text-sm space-y-1">
              <div className="flex justify-between py-1"><span>Price per unit</span><span>{fmt(data.break_even_inputs.price_per_unit)}</span></div>
              <div className="flex justify-between py-1"><span>Variable cost per unit</span><span>{fmt(data.break_even_inputs.variable_cost_per_unit)}</span></div>
              <div className="flex justify-between py-1"><span>Contribution margin</span><span>{fmt(be.contribution_margin)}</span></div>
              <div className="flex justify-between py-1"><span>Monthly fixed costs</span><span>{fmt(data.break_even_inputs.monthly_fixed_costs)}</span></div>
              <div className="flex justify-between py-1 font-bold border-t"><span>Break-even units / month</span>
                <span>{Math.ceil(be.units_per_month).toLocaleString()} units</span></div>
              <div className="flex justify-between py-1 font-semibold"><span>Break-even revenue / month</span><span>{fmt(be.revenue_per_month)}</span></div>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Set the unit price, variable cost per unit and monthly fixed costs in the
              Settings tab to see the break-even point.
            </p>
          )}
        </CardContent></Card>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Purchases / Assets / Loans — generic CRUD driven by config          */
/* ------------------------------------------------------------------ */

const SUBMODULES = {
  purchases: {
    key: 'purchases', title: 'Purchase', addLabel: 'Record Purchase',
    fields: [
      { key: 'date', label: 'Date', type: 'date', required: true },
      { key: 'item', label: 'Item', type: 'text', required: true, placeholder: 'e.g. Gypsum powder' },
      { key: 'category', label: 'Category', type: 'select', options: ['Raw Material', 'Packaging', 'Equipment', 'Other'] },
      { key: 'qty', label: 'Quantity', type: 'number', required: true },
      { key: 'unit_cost', label: 'Unit Cost (Tsh)', type: 'number', required: true },
      { key: 'supplier', label: 'Supplier', type: 'text' },
    ],
    columns: [
      { key: 'date', label: 'Date' },
      { key: 'item', label: 'Item' },
      { key: 'category', label: 'Category', badge: true },
      { key: 'qty', label: 'Qty', right: true, render: (r) => Number(r.qty).toLocaleString() },
      { key: 'unit_cost', label: 'Unit Cost', right: true, render: (r) => fmt(r.unit_cost) },
      { key: 'total', label: 'Total', right: true, bold: true, render: (r) => fmt(r.qty * r.unit_cost) },
      { key: 'supplier', label: 'Supplier' },
    ],
    total: (rows) => rows.reduce((s, r) => s + r.qty * r.unit_cost, 0),
  },
  assets: {
    key: 'assets', title: 'Asset', addLabel: 'Add Asset',
    fields: [
      { key: 'name', label: 'Asset Name', type: 'text', required: true, placeholder: 'e.g. Chalk moulding machine' },
      { key: 'category', label: 'Category', type: 'select', options: ['Machinery', 'Vehicle', 'Building', 'Furniture', 'Equipment', 'Other'] },
      { key: 'purchase_date', label: 'Purchase Date', type: 'date', required: true },
      { key: 'cost', label: 'Cost (Tsh)', type: 'number', required: true },
      { key: 'useful_life_years', label: 'Useful Life (years)', type: 'number', required: true },
    ],
    columns: [
      { key: 'name', label: 'Asset' },
      { key: 'category', label: 'Category', badge: true },
      { key: 'purchase_date', label: 'Purchased' },
      { key: 'cost', label: 'Cost', right: true, render: (r) => fmt(r.cost) },
      { key: 'useful_life_years', label: 'Life (yrs)', right: true },
    ],
    total: (rows) => rows.reduce((s, r) => s + (r.cost || 0), 0),
  },
  loans: {
    key: 'loans', title: 'Loan', addLabel: 'Add Loan',
    fields: [
      { key: 'lender', label: 'Lender', type: 'text', required: true, placeholder: 'e.g. NMB Bank' },
      { key: 'principal', label: 'Principal (Tsh)', type: 'number', required: true },
      { key: 'interest_rate', label: 'Interest Rate (%)', type: 'number' },
      { key: 'start_date', label: 'Start Date', type: 'date', required: true },
      { key: 'term_months', label: 'Term (months)', type: 'number' },
      { key: 'outstanding_balance', label: 'Outstanding Balance (Tsh)', type: 'number', required: true },
    ],
    columns: [
      { key: 'lender', label: 'Lender' },
      { key: 'principal', label: 'Principal', right: true, render: (r) => fmt(r.principal) },
      { key: 'interest_rate', label: 'Rate %', right: true },
      { key: 'start_date', label: 'Start' },
      { key: 'term_months', label: 'Term (mo)', right: true },
      { key: 'outstanding_balance', label: 'Outstanding', right: true, bold: true, render: (r) => fmt(r.outstanding_balance) },
    ],
    total: (rows) => rows.reduce((s, r) => s + (r.outstanding_balance || 0), 0),
  },
};

function SubModule({ cfg, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = Object.fromEntries(cfg.fields.map((f) => [f.key,
    f.type === 'date' ? new Date().toISOString().slice(0, 10) : f.type === 'select' ? f.options[0] : '']));
  const [form, setForm] = useState(empty);

  const fetchRows = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/${cfg.key}`, authHeaders());
      setRows(res.data);
    } catch (error) {
      toast.error(`Failed to load ${cfg.key}`);
    }
  }, [cfg.key]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      cfg.fields.forEach((f) => { if (f.type === 'number') payload[f.key] = Number(payload[f.key] || 0); });
      if (editing) {
        await axios.put(`${API}/finance/${cfg.key}/${editing.id}`, payload, authHeaders());
        toast.success(`${cfg.title} updated`);
      } else {
        await axios.post(`${API}/finance/${cfg.key}`, payload, authHeaders());
        toast.success(`${cfg.title} recorded`);
      }
      setOpen(false); setEditing(null); setForm(empty);
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${cfg.title.toLowerCase()}?`)) return;
    try {
      await axios.delete(`${API}/finance/${cfg.key}/${row.id}`, authHeaders());
      toast.success('Deleted');
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4 mt-2">
      <div className="flex justify-between items-center">
        <Badge variant="outline">{rows.length} records · {fmt(cfg.total(rows))}</Badge>
        <Button size="sm" data-testid={`add-${cfg.key}-button`}
          onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}>
          <Plus size={14} className="mr-1" /> {cfg.addLabel}
        </Button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {cfg.columns.map((c) => (
                <TableHead key={c.key} className={c.right ? 'text-right' : ''}>{c.label}</TableHead>
              ))}
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={cfg.columns.length + 1} className="text-center text-gray-400">
                No {cfg.key} recorded yet.
              </TableCell></TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id}>
                {cfg.columns.map((c) => (
                  <TableCell key={c.key}
                    className={`${c.right ? 'text-right' : ''} ${c.bold ? 'font-semibold' : ''} text-sm`}>
                    {c.badge
                      ? <Badge variant="outline">{row[c.key] || '-'}</Badge>
                      : c.render ? c.render(row) : (row[c.key] ?? '-')}
                  </TableCell>
                ))}
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="icon"
                    onClick={() => { setEditing(row); setForm({ ...empty, ...row }); setOpen(true); }}>
                    <Pencil size={14} />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" onClick={() => remove(row)}>
                      <Trash2 size={14} className="text-red-600" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${cfg.key}-dialog`}>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Add'} {cfg.title}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {cfg.fields.map((f) => (
                <div key={f.key} className="space-y-1">
                  <Label>{f.label}{f.required ? ' *' : ''}</Label>
                  {f.type === 'select' ? (
                    <select className="w-full border rounded-md h-9 px-2 text-sm bg-white" value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}>
                      {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <Input type={f.type} required={f.required} value={form[f.key] ?? ''}
                      placeholder={f.placeholder} min={f.type === 'number' ? '0' : undefined}
                      step={f.type === 'number' ? 'any' : undefined}
                      data-testid={`${cfg.key}-${f.key}-input`}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                  )}
                </div>
              ))}
            </div>
            <Button type="submit" className="w-full" data-testid={`save-${cfg.key}-button`}>
              {editing ? 'Save Changes' : cfg.addLabel}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProfitLoss() {
  const [period, setPeriod] = useState('monthly');
  const [year, setYear] = useState('');
  const [data, setData] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const params = { period };
      if (year) params.year = year;
      const res = await axios.get(`${API}/finance/profit-loss`, { ...authHeaders(), params });
      setData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load profit & loss');
    }
  }, [period, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (!data) return <p className="text-gray-400 mt-4">Loading…</p>;

  const nonZero = data.buckets.some((b) => b.income || b.expenses);

  return (
    <div className="space-y-4 mt-2">
      <div className="flex flex-wrap items-center gap-2">
        {['monthly', 'quarterly', 'yearly'].map((p) => (
          <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'} className="capitalize"
            onClick={() => setPeriod(p)} data-testid={`pnl-${p}`}>{p}</Button>
        ))}
        {period !== 'yearly' && (
          <Input type="number" className="w-28" placeholder={`FY ${data.fy_year}`} value={year}
            onChange={(e) => setYear(e.target.value)} title="Financial year (the year it ends in)" />
        )}
        <Badge variant="outline" className="ml-auto">{data.fy_label} · FY starts {MONTHS[data.fy_start_month - 1]}</Badge>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <TrendingUp className="text-green-600" />
          <div><p className="text-xs text-gray-500">Total Income</p>
            <p className="text-xl font-bold text-green-700" data-testid="pnl-income">{fmt(data.total_income)}</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <TrendingDown className="text-red-500" />
          <div><p className="text-xs text-gray-500">Total Expenses</p>
            <p className="text-xl font-bold text-red-600" data-testid="pnl-expenses">{fmt(data.total_expenses)}</p></div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 flex items-center gap-3">
          <Wallet className={data.net_profit >= 0 ? 'text-green-600' : 'text-red-500'} />
          <div><p className="text-xs text-gray-500">Net Profit</p>
            <p className={`text-xl font-bold ${data.net_profit >= 0 ? 'text-green-700' : 'text-red-600'}`}
              data-testid="pnl-profit">{fmt(data.net_profit)}</p></div>
        </CardContent></Card>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{period === 'monthly' ? 'Month' : period === 'quarterly' ? 'Quarter' : 'Financial Year'}</TableHead>
              <TableHead className="text-right">Income</TableHead>
              <TableHead className="text-right">Expenses</TableHead>
              <TableHead className="text-right">Profit</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {!nonZero && (
              <TableRow><TableCell colSpan={4} className="text-center text-gray-400">
                No financial activity in this period yet.
              </TableCell></TableRow>
            )}
            {nonZero && data.buckets.map((b) => (
              <TableRow key={b.label}>
                <TableCell className="font-medium">{b.label}</TableCell>
                <TableCell className="text-right text-green-700">{fmt(b.income)}</TableCell>
                <TableCell className="text-right text-red-600">{fmt(b.expenses)}</TableCell>
                <TableCell className={`text-right font-semibold ${b.profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                  {fmt(b.profit)}
                </TableCell>
              </TableRow>
            ))}
            {nonZero && (
              <TableRow className="bg-slate-50 font-bold">
                <TableCell>Total</TableCell>
                <TableCell className="text-right text-green-700">{fmt(data.total_income)}</TableCell>
                <TableCell className="text-right text-red-600">{fmt(data.total_expenses)}</TableCell>
                <TableCell className={`text-right ${data.net_profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                  {fmt(data.net_profit)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {Object.keys(data.expenses_by_category).length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card><CardContent className="pt-4">
            <h3 className="font-semibold mb-2">Expenses by Category</h3>
            {Object.entries(data.expenses_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
              <div key={cat} className="flex justify-between text-sm py-1 border-b last:border-0">
                <span className="capitalize">{cat.replace(/_/g, ' ')}</span><span>{fmt(amt)}</span>
              </div>
            ))}
          </CardContent></Card>
          <Card><CardContent className="pt-4">
            <h3 className="font-semibold mb-2">Income by Source</h3>
            {Object.entries(data.income_by_source).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
              <div key={cat} className="flex justify-between text-sm py-1 border-b last:border-0">
                <span className="capitalize">{cat.replace(/_/g, ' ')}</span><span>{fmt(amt)}</span>
              </div>
            ))}
          </CardContent></Card>
        </div>
      )}
    </div>
  );
}

function Entries({ kind, categories, isAdmin }) {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = { kind, date: new Date().toISOString().slice(0, 10), category: categories[0] || 'other',
    amount: '', description: '', payee: '', payment_method: 'cash', reference: '' };
  const [form, setForm] = useState(empty);

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/finance/entries`, { ...authHeaders(), params: { kind } });
      setEntries(res.data);
    } catch (error) {
      toast.error('Failed to load entries');
    }
  }, [kind]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, amount: Number(form.amount) };
      if (editing) {
        await axios.put(`${API}/finance/entries/${editing.id}`, payload, authHeaders());
        toast.success('Entry updated');
      } else {
        await axios.post(`${API}/finance/entries`, payload, authHeaders());
        toast.success(kind === 'expense' ? 'Expense recorded' : 'Income recorded');
      }
      setOpen(false); setEditing(null); setForm(empty);
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Delete this ${kind} of ${fmt(entry.amount)}?`)) return;
    try {
      await axios.delete(`${API}/finance/entries/${entry.id}`, authHeaders());
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    }
  };

  const total = entries.reduce((s, e) => s + (e.amount || 0), 0);

  return (
    <div className="space-y-4 mt-2">
      <div className="flex justify-between items-center">
        <Badge variant="outline">{entries.length} entries · {fmt(total)}</Badge>
        <Button size="sm" data-testid={`add-${kind}-button`}
          onClick={() => { setEditing(null); setForm(empty); setOpen(true); }}>
          <Plus size={14} className="mr-1" /> {kind === 'expense' ? 'Record Expense' : 'Record Income'}
        </Button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>{kind === 'expense' ? 'Payee' : 'Source'}</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>By</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center text-gray-400">
                No {kind} entries yet.
              </TableCell></TableRow>
            )}
            {entries.map((entry) => (
              <TableRow key={entry.id}>
                <TableCell>{entry.date}</TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{(entry.category || '').replace(/_/g, ' ')}</Badge></TableCell>
                <TableCell className="text-sm">{entry.description || '-'}</TableCell>
                <TableCell className="text-sm">{entry.payee || '-'}</TableCell>
                <TableCell className={`text-right font-semibold ${kind === 'expense' ? 'text-red-600' : 'text-green-700'}`}>
                  {fmt(entry.amount)}
                </TableCell>
                <TableCell className="text-xs text-gray-500">{entry.created_by}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon"
                    onClick={() => { setEditing(entry); setForm({ ...empty, ...entry }); setOpen(true); }}>
                    <Pencil size={14} />
                  </Button>
                  {isAdmin && (
                    <Button variant="ghost" size="icon" onClick={() => remove(entry)}>
                      <Trash2 size={14} className="text-red-600" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${kind}-dialog`}>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Record'} {kind === 'expense' ? 'Expense' : 'Income'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Date *</Label>
                <Input type="date" required value={form.date} data-testid={`${kind}-date-input`}
                  onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Amount (Tsh) *</Label>
                <Input type="number" min="1" required value={form.amount} data-testid={`${kind}-amount-input`}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              </div>
            </div>
            <div className="space-y-1">
              <Label>Category *</Label>
              <select className="w-full border rounded-md h-9 px-2 text-sm bg-white" value={form.category}
                data-testid={`${kind}-category-select`}
                onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {categories.map((c) => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <Label>Description</Label>
              <Input value={form.description || ''} placeholder={kind === 'expense' ? 'e.g. August salaries' : 'e.g. interest earned'}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>{kind === 'expense' ? 'Payee' : 'Source'}</Label>
                <Input value={form.payee || ''} onChange={(e) => setForm({ ...form, payee: e.target.value })} />
              </div>
              <div className="space-y-1">
                <Label>Reference</Label>
                <Input value={form.reference || ''} placeholder="receipt / cheque no."
                  onChange={(e) => setForm({ ...form, reference: e.target.value })} />
              </div>
            </div>
            <Button type="submit" className="w-full" data-testid={`save-${kind}-button`}>
              {editing ? 'Save Changes' : kind === 'expense' ? 'Record Expense' : 'Record Income'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FinanceSettings({ settings, onSaved }) {
  const [month, setMonth] = useState(settings.fy_start_month);
  const [cash, setCash] = useState(settings.cash_on_hand || 0);
  const [be, setBe] = useState(settings.break_even || { price_per_unit: 0, variable_cost_per_unit: 0, monthly_fixed_costs: 0 });

  useEffect(() => {
    setMonth(settings.fy_start_month);
    setCash(settings.cash_on_hand || 0);
    setBe(settings.break_even || { price_per_unit: 0, variable_cost_per_unit: 0, monthly_fixed_costs: 0 });
  }, [settings]);

  const save = async () => {
    try {
      await axios.put(`${API}/finance/settings`, {
        fy_start_month: Number(month),
        cash_on_hand: Number(cash) || 0,
        break_even: {
          price_per_unit: Number(be.price_per_unit) || 0,
          variable_cost_per_unit: Number(be.variable_cost_per_unit) || 0,
          monthly_fixed_costs: Number(be.monthly_fixed_costs) || 0,
        },
      }, authHeaders());
      toast.success('Settings saved');
      onSaved();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Save failed');
    }
  };

  const endMonth = MONTHS[(Number(month) + 10) % 12];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2 max-w-4xl">
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><Settings size={16} /> Financial Year &amp; Cash</div>
          <div className="space-y-1">
            <Label>Financial year starts in</Label>
            <select className="w-full border rounded-md h-9 px-2 text-sm bg-white" value={month}
              data-testid="fy-start-select" onChange={(e) => setMonth(e.target.value)}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <p className="text-sm text-gray-500">
            The financial year will run from <b>1 {MONTHS[month - 1]}</b> to <b>end of {endMonth}</b>.
          </p>
          <div className="space-y-1">
            <Label>Opening cash balance (Tsh)</Label>
            <Input type="number" min="0" value={cash} data-testid="cash-on-hand-input"
              onChange={(e) => setCash(e.target.value)} />
            <p className="text-xs text-gray-500">Cash position on the Overview tab = this amount + recorded net profit.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><Scale size={16} /> Break-even Inputs</div>
          <div className="space-y-1">
            <Label>Selling price per unit (Tsh)</Label>
            <Input type="number" min="0" value={be.price_per_unit} data-testid="be-price-input"
              onChange={(e) => setBe({ ...be, price_per_unit: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Variable cost per unit (Tsh)</Label>
            <Input type="number" min="0" value={be.variable_cost_per_unit} data-testid="be-varcost-input"
              onChange={(e) => setBe({ ...be, variable_cost_per_unit: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>Monthly fixed costs (Tsh)</Label>
            <Input type="number" min="0" value={be.monthly_fixed_costs} data-testid="be-fixed-input"
              onChange={(e) => setBe({ ...be, monthly_fixed_costs: e.target.value })} />
          </div>
        </CardContent>
      </Card>

      <div className="md:col-span-2">
        <Button onClick={save} data-testid="save-fy-button">Save Settings</Button>
      </div>
    </div>
  );
}
